import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, MessageCircle, X, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useSite, waLinkFor } from "@/lib/SiteContext";
import { EASE } from "./primitives";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]";
const inputCls =
  "w-full rounded-xl border border-line bg-cream/50 px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-rose";

export default function CheckoutModal({ product, onClose }) {
  const { brand, contact, razorpay } = useSite();
  const [step, setStep] = useState("form"); // form | processing | success | failed
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setError(""); };

  const startPayment = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return setError("Please enter your name");
    if (form.phone.replace(/\D/g, "").length < 10) return setError("Enter a valid phone number");
    setError("");
    setStep("processing");
    try {
      const { data } = await api.post("/payments/order", {
        product_id: product.id,
        name: form.name,
        phone: form.phone,
        email: form.email,
      });
      setOrder(data);
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("checkout-load-failed");

      const rzp = new window.Razorpay({
        key: data.key_id || razorpay.key_id,
        amount: data.amount,
        currency: data.currency || "INR",
        name: brand.name,
        description: product.name,
        order_id: data.order_id,
        prefill: { name: form.name, contact: form.phone, email: form.email },
        theme: { color: "#C48B81" },
        modal: {
          ondismiss: () => setStep("form"),
        },
        handler: async (response) => {
          try {
            const { data: verified } = await api.post("/payments/verify", response);
            setOrder(verified);
            setStep("success");
          } catch {
            setStep("failed");
          }
        },
      });
      rzp.on("payment.failed", async (resp) => {
        try {
          await api.post("/payments/failed", { razorpay_order_id: resp?.error?.metadata?.order_id || data.order_id });
        } catch { /* best effort */ }
        setStep("failed");
      });
      rzp.open();
      setStep("form");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        (Array.isArray(detail) ? detail[0]?.msg : detail) ||
          "Could not start the payment. Please try again or order on WhatsApp.",
      );
      setStep("form");
    }
  };

  const waMessage = product
    ? `Hello ${brand.name}! I'd like to order the ${product.name} (${product.price}).`
    : "";

  return (
    <AnimatePresence>
      {product && (
        <div className="fixed inset-0 z-[90] flex items-end justify-center md:items-center md:p-8">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-ink/60 backdrop-blur-sm" aria-hidden />
          <motion.div
            data-testid="checkout-modal"
            role="dialog"
            aria-modal="true"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="relative w-full max-w-md rounded-t-3xl bg-white p-7 md:rounded-3xl md:p-9"
          >
            <button data-testid="checkout-close" onClick={onClose} aria-label="Close checkout" className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-line transition-colors hover:bg-ink hover:text-cream">
              <X size={16} />
            </button>

            {step === "success" ? (
              <div data-testid="payment-success" className="flex flex-col items-center gap-4 py-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                  <CheckCircle2 size={30} className="text-emerald-600" />
                </span>
                <h3 className="font-serif text-3xl font-medium">Payment Successful</h3>
                <p className="max-w-xs text-sm leading-relaxed text-[#5C5C5C]">
                  Thank you, {form.name.split(" ")[0]}! Your order for <strong>{product.name}</strong> is confirmed. We'll reach out on WhatsApp shortly.
                </p>
                <div className="w-full rounded-2xl bg-cream p-4 text-left text-xs">
                  <p><span className="font-semibold">Order Ref:</span> {order?.razorpay_order_id}</p>
                  {order?.razorpay_payment_id && <p className="mt-1"><span className="font-semibold">Payment ID:</span> {order.razorpay_payment_id}</p>}
                  <p className="mt-1"><span className="font-semibold">Status:</span> {order?.status || "Paid"}</p>
                </div>
                <a href={waLinkFor(contact, waMessage)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#1eb857]">
                  <MessageCircle size={15} /> Confirm details on WhatsApp
                </a>
              </div>
            ) : step === "failed" ? (
              <div data-testid="payment-failed" className="flex flex-col items-center gap-4 py-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                  <XCircle size={30} className="text-red-500" />
                </span>
                <h3 className="font-serif text-3xl font-medium">Payment Not Completed</h3>
                <p className="max-w-xs text-sm leading-relaxed text-[#5C5C5C]">
                  Your payment failed or was cancelled. No money has been captured for an incomplete payment.
                </p>
                <button data-testid="payment-retry-button" onClick={() => setStep("form")} className="rounded-full bg-ink px-8 py-3.5 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white">
                  Try Again
                </button>
                <a href={waLinkFor(contact, waMessage)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-[#25D366] hover:underline">
                  <MessageCircle size={15} /> Order on WhatsApp instead
                </a>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-2xl font-medium md:text-3xl">Buy {product.name}</h3>
                <p className="mt-1 text-sm text-[#5C5C5C]">{product.price} · Secure payment via Razorpay</p>
                <form data-testid="checkout-form" onSubmit={startPayment} className="mt-6 flex flex-col gap-4">
                  <div>
                    <label htmlFor="co-name" className={labelCls}>Name *</label>
                    <input id="co-name" data-testid="checkout-name-input" type="text" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} autoComplete="name" />
                  </div>
                  <div>
                    <label htmlFor="co-phone" className={labelCls}>Phone *</label>
                    <input id="co-phone" data-testid="checkout-phone-input" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} autoComplete="tel" />
                  </div>
                  <div>
                    <label htmlFor="co-email" className={labelCls}>Email</label>
                    <input id="co-email" data-testid="checkout-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} autoComplete="email" />
                  </div>
                  {error && <p data-testid="checkout-error" className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600">{error}</p>}
                  <button data-testid="checkout-pay-button" type="submit" disabled={step === "processing"} className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white disabled:opacity-60">
                    {step === "processing" ? <><Loader2 size={16} className="animate-spin" /> Preparing payment…</> : `Pay ${product.price}`}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
